import React, { useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  IconButton,
  Box,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '@apollo/client/react';
import { GET_COMPANIES } from '../reports.graphql';
import { CompanyOverviewModel, CompanyOverviewProps } from '../../interfaces';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid-pro/locales';

import CompanyDetail from './CompanyDetail';
import { GeneralExcelExport, GeneralExcelExportProps } from '../brreg';

type CompanyOverviewGridRow = CompanyOverviewModel & {
  id: string;
};

export default function CompanyOverview(props: CompanyOverviewProps) {
  const { onBackClick } = props;
  const [filter, setFilter] = useState('alle');
  const [searchText, setSearchText] = useState('');
  const { loading, data } = useQuery<{ companyOverview: CompanyOverviewModel[] }>(GET_COMPANIES, {
    fetchPolicy: 'cache-and-network',
  });
  const [selectedOverview, setSelectedOverview] = React.useState('companyOverview');
  const [selectedOrgNumber, setSelectedOrgNumber] = React.useState('orgNumber');
  const [selectedCompany, setSelectedCompany] = React.useState('companyName');

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    setFilter(event.target.value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const filteredData = loading
    ? undefined
    : data?.companyOverview.filter((company) => {
      if (filter === 'alle') {
        return true;
      } else if (filter === 'største') {
        return company.licenseCount > 10;
      } else {
        return false;
      }
    });

  const searchedData = filteredData?.filter((company) =>
    Object.values(company).some((value) => value?.toString().toLowerCase().startsWith(searchText.toLowerCase())),
  );

  const gridRows = useMemo(
    () =>
      (searchedData ?? []).map((company, index) => ({
        ...company,
        id: `${company.orgNumber}-${company.companyName}-${index}`,
      })),
    [searchedData],
  );

  const handleLinkClick = (overview: string, orgNumber: string, companyName: string) => {
    setSelectedOrgNumber(orgNumber);
    setSelectedOverview(overview);
    setSelectedCompany(companyName);
  };

  const arrHeading: string[] = ['Org. Nummer', 'Firma Navn', 'Brreg', 'License', 'Bruke'];
  const dataSet = filteredData && filteredData.map((x) => [x.orgNumber, x.companyName, x.brreg, x.licenseCount, x.userCount]);
  const workBook = 'KUNDE_LISTE';
  const fileName = 'kunde_liste.xlsx';
  const propsExcel: GeneralExcelExportProps = {
    dataSet: JSON.stringify(dataSet),
    headings: JSON.stringify(arrHeading),
    workBookName: workBook,
    fileName: fileName,
  };

  const columns = useMemo<GridColDef<CompanyOverviewGridRow>[]>(
    () => [
      { field: 'orgNumber', headerName: 'Org. Nummer', minWidth: 160, flex: 1, align: 'left', headerAlign: 'left' },
      {
        field: 'companyName',
        headerName: 'Firma Navn',
        minWidth: 160,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <Button
            size="small"
            onClick={() => handleLinkClick('companyDetail', params.row.orgNumber, params.row.companyName)}
            sx={{ minWidth: 0, px: 0, textDecoration: 'underline', fontWeight: 600 }}>
            {params.value}
          </Button>
        ),
      },
      { field: 'brreg', headerName: 'Brreg', minWidth: 160, flex: 1, align: 'left', headerAlign: 'left' },
      { field: 'licenseCount', headerName: 'Lisenser', minWidth: 160, flex: 1, align: 'left', headerAlign: 'left' },
      { field: 'userCount', headerName: 'Brukere', minWidth: 160, flex: 1, align: 'left', headerAlign: 'left' },
    ],
    [],
  );

  return (
    <>
      {selectedOverview === 'companyOverview' && (
        <Stack spacing={2}>
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                    <IconButton onClick={onBackClick} title="Tilbake" size="large">
                      <ArrowBackIcon />
                    </IconButton>
                    <Box>
                      <Typography variant="h6">Per Firma</Typography>
                      <Typography variant="body2" sx={{
                        color: "text.secondary"
                      }}>
                        Velg firma for å åpne detaljert bruksrapport med produkt- og brukerperspektiv.
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: { xs: 'stretch', md: 'center' } }}>
                    <FormControl sx={{ minWidth: 200 }} size="small">
                      <InputLabel id="company-filter-label">Filtrere på</InputLabel>
                      <Select labelId="company-filter-label" value={filter} label="Filtrere på" onChange={handleFilterChange}>
                        <MenuItem value="alle">Alle</MenuItem>
                        <MenuItem value="største">Største</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField label="Søk" value={searchText} onChange={handleSearchChange} size="small" sx={{ flexGrow: 1 }} />
                    <GeneralExcelExport {...propsExcel} />
                  </Stack>
                </Stack>
              </Paper>

              <Paper elevation={1} sx={{ p: 2 }}>
                <DataGridPro
                  autoHeight
                  density="compact"
                  rows={gridRows}
                  columns={columns}
                  getRowId={(row) => row.id}
                  localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
                  pagination
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 50,
                        page: 0,
                      },
                    },
                  }}
                  sx={{
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 700,
                    },
                  }}
                />
              </Paper>
            </>
          )}
        </Stack>
      )}
      {selectedOverview === 'companyDetail' && (
        <CompanyDetail onBackClick={() => setSelectedOverview('companyOverview')} orgNumber={selectedOrgNumber} companyName={selectedCompany} />
      )}
    </>
  );
}
